import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ShieldCheck, ArrowRight, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import SparkLogo from '../components/SparkLogo';

const AdminLogin = () => {
  const { user, signInWithEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const denied = Boolean(location.state?.denied);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // If a valid admin session already exists, bounce straight to /admin
  useEffect(() => {
    let mounted = true;
    const check = async () => {
      if (!user) return;
      const { data } = await supabase.rpc('is_admin');
      if (mounted && data === true) navigate('/admin', { replace: true });
    };
    check();
    return () => { mounted = false; };
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await signInWithEmail(email, password);
      const { data, error: rpcErr } = await supabase.rpc('is_admin');
      if (rpcErr) throw rpcErr;
      if (data !== true) {
        // Not an admin -> immediately sign out so no lingering session on /admin
        await supabase.auth.signOut();
        throw new Error('Esta conta não possui permissão administrativa.');
      }
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err.message || 'Falha no login.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white flex items-center justify-center px-5 py-12 overflow-hidden relative">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(79,172,254,0.15), transparent 70%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,167,38,0.12), transparent 70%)' }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="flex justify-center mb-6"><SparkLogo height={64} /></div>

        <div className="glass-card rounded-3xl p-7 sm:p-8" data-testid="admin-login-card">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] uppercase tracking-widest bg-[#ffa726]/10 border border-[#ffa726]/30 text-[#ffd54f] mb-3">
              <ShieldCheck size={12} /> Acesso restrito
            </div>
            <h1 className="heading-display text-2xl font-bold text-white" data-testid="admin-login-title">GetSpark Admin</h1>
            <p className="mt-1.5 text-xs text-slate-400">Entre com uma conta com permissão administrativa.</p>
          </div>

          {denied && (
            <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 text-xs text-red-300 px-3 py-2 flex items-start gap-2" data-testid="admin-denied">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span>Acesso negado. Sua conta não possui permissão administrativa.</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="admin-login-form">
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="email" required
                data-testid="admin-input-email"
                autoComplete="email"
                placeholder="e-mail do administrador"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50"
              />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type={showPassword ? 'text' : 'password'} required minLength={6}
                data-testid="admin-input-password"
                autoComplete="current-password"
                placeholder="senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-12 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="admin-toggle-password"
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] active:bg-white/10 transition-colors touch-manipulation"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {error && (
              <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="admin-login-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 btn-primary"
            >
              {loading ? 'Aguarde...' : (<>Entrar como admin <ArrowRight size={14} /></>)}
            </button>
          </form>

          <p className="mt-5 text-center text-[11px] text-slate-500">
            Toda tentativa de acesso é registrada no Supabase Auth.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
